import React, { ReactText, useState } from "react"
import ReactModal from "react-modal"
import { useMediaQuery } from "react-responsive"
import tw, { styled, css } from "twin.macro"

import SlideSection from "../components/slideSection"
import NextPageButton from "../components/nextPageButton"
import { modalStyles } from "../utils/modalStyles"
import LearnDetail from "../components/learnDetail"
import CloseIcon from "../svgs/closeIcon"
import Icon3 from "../icons/icon3"
import Icon1 from "../icons/icon1"
import Icon2 from "../icons/icon2"
import Container from "../components/container"
import { SlideProps } from "."
import NextPageContainer from "../components/nextPageContainer"

const Features = styled.div`
  ${tw`flex flex-col md:flex-row justify-center items-center`}
`

const Feature = styled.div`
  max-width: 20rem;
  padding: 0 2.1875rem;
  ${tw`flex flex-col items-center md:w-1/3 mb-16 md:mb-0`}
`

const FeatureName = styled.h3`
  ${tw`font-sans text-lg text-sm font-bold uppercase mt-4 md:mt-6 mb-4 text-center tracking-wider whitespace-pre-line`}
`

const FeatureDescription = styled.p`
  ${tw`font-sans text-center text-xs mb-6 tracking-wide whitespace-pre-line`}
`

const LearnMoreLink = styled.a`
  font-size: 0.6875rem;
  ${tw`font-sans uppercase underline hover:text-yellow-400 tracking-tight text-center block`}
`

interface FeatureData {
  icon: React.FC
  title: ReactText
  description: ReactText
  details?: FeatureDetails[]
}

interface FeatureDetails {
  title?: string
  text: string[]
}

const features: FeatureData[] = [
  {
    icon: Icon3,
    title: "Get NZQA recognised,\nfor free",
    description:
      "Put your passion to work and add valuable knowledge to your CV.",
    details: [
      {
        title: "What's a MITO Micro?",
        text: [
          "It’s a bite-sized online course that recognises achievement of specific foundation knowledge. The MITO Micro&reg; Introduction to Automotive Engineering is a great way to put your passion to work and may help you to get a foot in the door of the automotive industry. And you’ll be rewarded with an NZQA-recognised micro-credential for your efforts.",
        ],
      },
      {
        title: "Why should I do it now?",
        text: [
          "This MITO Micro&reg; is free. So, whether you’re just starting out or considering switching careers, there’s never been a better time to boost your CV with MITO.",
          "N.B. This course has been designed for people who are no longer at secondary school."
        ],
      },
    ],
  },
  {
    icon: Icon1,
    title: "Learn the basics\nin your own time",
    description: "Work at your own speed with interactive online learning.",
    details: [
      {
        title: "How does it work?",
        text: [
          "This is an introductory course with Level 2 and 3 unit standards taken from a full NZQA-approved automotive programme, covering the foundation knowledge that will underpin any future learning. It is completed online with videos, interactive modules and theory assessments accessed via any device, so you can work wherever you have access to the internet. Because there’s no practical training involved, you don’t need to be employed in the automotive industry to complete this course.",
        ],
      },
      {
        title: "How long will it take?",
        text: [
          "That depends on you. It can take up to five months (at a rate of 10 hours per week). If you have more time to burn you can get through it faster, but it must be completed within 6 months of your enrolment.",
        ],
      },
    ],
  },
  {
    icon: Icon2,
    title: "Get noticed,\nget ahead",
    description:
      "Grow knowledge that could help you get a full apprenticeship.",
    details: [
      {
        title: "What's in it for me?",
        text: [
          "A MITO Micro&reg; Introduction to Automotive Engineering on your CV demonstrates a sound knowledge of the basics. This can help you get the attention of employers who are looking for an automotive apprentice and could lead to a rewarding career in the industry.",
        ],
      },
      {
        title: "What else can I do to get ahead?",
        text: [
          "MITO is the industry training organisation for the automotive industry, facilitating training across New Zealand. On the MITO Job Hub you can look for current job vacancies. Plus, you can create a free jobseeker profile and promote yourself to employers who may have current job opportunities.",
          'Visit <a style="text-decoration: underline;" href="https://www.mito.org.nz/jobhub" target=_blank>mito.nz/jobhub</a> to create your online profile.',
        ],
      },
    ],
  },
]

const FeaturesSlide: React.SFC<SlideProps> = ({
  slideVisible,
  nextButtonClicked,
}) => {
  const [featureIndex, setFeatureIndex] = useState<number>(-1)

  const isSmall = useMediaQuery({ query: "(max-width: 767px)" })

  return (
    <SlideSection autoHeightResponsive>
      <div css={[tw`flex flex-col justify-center items-center`]}>
        <Container>
          <Features>
            {features.map((feature, index) => {
              const delay = `transition-delay: ${
                slideVisible ? 250 + index * 175 : 0
              }ms`
              return (
                <Feature key={index}>
                  {/* Fade + translate the icon in */}
                  <div
                    css={[
                      css`
                        ${delay}
                      `,
                      tw`transition duration-1000 ease-out`,
                      !slideVisible && tw`opacity-0 transform -translate-y-8`,
                    ]}
                  >
                    <feature.icon />
                  </div>
                  {/* Fade the text in */}
                  <div
                    css={[
                      css`
                        ${delay}
                      `,
                      tw`transition duration-1000 ease-out`,
                      !slideVisible && tw`opacity-0`,
                    ]}
                  >
                    <FeatureName>{feature.title}</FeatureName>
                    <FeatureDescription>
                      {feature.description}
                    </FeatureDescription>
                    {/* Show learn more if it has more  */}
                    {feature.details?.length ?? 0 > 0 ? (
                      <LearnMoreLink
                        href="#"
                        onClick={() => setFeatureIndex(index)}
                      >
                        Learn more
                      </LearnMoreLink>
                    ) : undefined}
                  </div>
                </Feature>
              )
            })}
          </Features>
        </Container>

        <NextPageContainer>
          <NextPageButton
            onClick={nextButtonClicked}
            label="How fast can I get there"
          />
        </NextPageContainer>
      </div>
      {/* When we select 'show more' display it in a modal */}
      <ReactModal
        style={modalStyles(isSmall)}
        isOpen={featureIndex >= 0}
        onRequestClose={() => setFeatureIndex(-1)}
      >
        {featureIndex >= 0 && features[featureIndex]?.details && (
          <LearnDetail items={features[featureIndex].details!} />
        )}
        <div css={[tw`absolute top-0 right-0`]}>
          <button
            css={[tw`p-4 focus:outline-none`]}
            onClick={() => setFeatureIndex(-1)}
          >
            <CloseIcon />
          </button>
        </div>
      </ReactModal>
    </SlideSection>
  )
}

export default FeaturesSlide
